import { createTheme } from '@mui/material/styles';

const GlobalStylingTheme = createTheme({
  statuscolors: {
    New: '#09929a',
    approved: '#7bc228',
    pending: '#ffb706',
    rejected: '#f44336',
    update: '#d9006e',
    withdrawn: '#c5d200',
  },

  palette: {
    primary: {
      main: '#1A1A1A',
      contrastText: '#fff',
    },

    secondary: {
      main: '#0288d1',
      light: '#5eb8ff',
      dark: '#005b9f',
      contrastText: '#fff',
    },

    background: {
      paper: '#ffffff',
    },
    text: {
      primary: '#1A1A1A',
    },
    customColors: {
      bismark: '#354ed6',
      panache: '#EDF4ED',
    },
  },

  /* ================== TYPOGRAPHY (UPDATED) ================== */
  typography: {
    fontFamily: 'DM Sans',
    fontSize: 14,

    h1: {
      fontSize: '2rem', // 32px
      fontWeight: 700,
    },
    h2: {
      fontSize: '1.5rem', // 24px
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.25rem', // 20px
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.125rem', // 18px
      fontWeight: 500,
    },
    h5: {
      fontSize: '1rem', // 16px
      fontWeight: 500,
      color: '#545353 !important',
    },
    h6: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      color: '#545353 !important',
    },

    body1: {
      fontSize: '0.875rem', // 14px
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.8125rem', // 13px
      fontWeight: 400,
    },

    subtitle1: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '0.75rem', // 12px
      fontWeight: 500,
    },

    caption: {
      fontSize: '0.75rem', // 12px
      fontWeight: 400,
    },

    overline: {
      fontSize: '0.6875rem', // 11px
      fontWeight: 500,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },

    button: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      textTransform: 'none',
    },
  },

  components: {
    MuiAvatar: {
      styleOverrides: {
        root: {
          color: '#545353',
          backgroundColor: '#fff',
          border: '2px solid #545353',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'DM Sans',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'DM Sans',
          textTransform: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          fontFamily: 'DM Sans',
        },
        shrink: {
          fontSize: '13px', // 👈 bigger than default
          transform: 'translate(0, -6px) scale(1)', // remove extra scaling
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: 'DM Sans',
          fontSize: '0.875rem', // input text unchanged
        },

        input: {
          /* fallback (some variants) */
          '&::placeholder': {
            fontSize: '1rem',
            opacity: 0.65,
          },
          shrink: {
            fontSize: '13px',
            transform: 'translate(0, -6px) scale(1)',
          },
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: 'DM Sans',
        },
        shrink: {
          fontSize: '13px', // 👈 bigger than default
          transform: 'translate(0, -6px) scale(1)', // remove extra scaling
        },
      },
    },
    MuiTextareaAutosize: {
      styleOverrides: {
        root: {
          fontFamily: 'DM Sans',
        },
      },
    },

    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#FFCC00 !important',
            color: '#1A1A1A !important',
            borderRadius: '12px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            fontWeight: 600,
            border: 'none',
          },

          '&.Mui-selected:hover': {
            backgroundColor: '#FFCC00',
          },
        },
      },
    },

    /* ================= YEAR ================= */
    MuiPickersYear: {
      styleOverrides: {
        yearButton: {
          '&.Mui-selected': {
            backgroundColor: '#FFCC00 !important',
            color: '#1A1A1A !important',
            borderRadius: '12px',
            fontWeight: 600,
          },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#000',

          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.04)',
          },

          '&.Mui-checked': {
            color: '#000',
          },

          '&.Mui-disabled': {
            color: '#9E9E9E',
          },
        },
      },
    },

    /* ---------------- Tabs ---------------- */
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 'auto',
          paddingLeft: '8px',
        },

        scroller: {
          padding: '8px 0',
        },
        indicator: {
          height: 2,
          borderRadius: 2,
          background: '#FFCC00',
          width: '0px !important',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem', // ✅ 14px
          padding: '0px 12px', // ✅ required padding
          minHeight: 'auto',
          minWidth: 'auto',
          height: '32px',
          '&.Mui-selected': {
            backgroundImage:
              'linear-gradient(110.36deg, rgb(255, 223, 94) 0%, rgb(243, 182, 0) 100%)',
            color: '#1A1A1A',

            borderRadius: '8px !important',
            '& .MuiSvgIcon-root': {
              color: '#1A1A1A',
            },
          },
        },
      },
    },

    /* ---------------- Typography ---------------- */
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'DM Sans',
        },
      },
    },

    /* ---------------- Icons ---------------- */
    MuiSvgIcon: {
      styleOverrides: {
        colorPrimary: {
          color: '#354ed6',
        },
        colorInfo: {
          color: '#454c59',
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        colorPrimary: {
          color: '#354ed6',
        },
        colorInfo: {
          color: '#535454',
        },
      },
    },

    /* ---------------- Radio ---------------- */
    MuiRadio: {
      styleOverrides: {
        colorPrimary: {
          color: '#000',
          '&.Mui-checked': {
            color: '#354ed6',
          },
        },
      },
    },

    /* ---------------- Buttons ---------------- */
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'DM Sans',
          textTransform: 'none',

          '&.Mui-disabled': {
            backgroundColor: '#E0E0E0',
            color: '#9E9E9E',
          },
        },

        containedPrimary: {
          backgroundImage:
            'linear-gradient(110.36deg, rgb(255, 223, 94) 0%, rgb(243, 182, 0) 100%)',
          color: '#000',
          boxShadow: 'none',

          '&:hover': {
            backgroundImage:
              'linear-gradient(110.36deg, rgb(243, 182, 0) 0%, rgb(255, 223, 94) 100%)',
          },
          '&.Mui-disabled': {
            backgroundImage: 'none',
            backgroundColor: '#E0E0E0',
            color: '#9E9E9E',
          },
        },
        containedWarning: {
          backgroundImage: 'linear-gradient(92.73deg, #FF547A 0.22%, #FF765E 100%)',
          color: '#fff',
          boxShadow: 'none',

          '&:hover': {
            backgroundImage: 'linear-gradient(92.73deg, #FF765E 0.22%, #FF547A 100%)',
          },
          '&.Mui-disabled': {
            backgroundImage: 'none',
            backgroundColor: '#E0E0E0',
            color: '#9E9E9E',
          },
        },
      },
    },

    /* ---------------- Inputs ---------------- */

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: 'DM Sans',
          fontSize: '0.75rem', // 12px
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: 'DM Sans',
          fontSize: '0.875rem', // 14px
        },
      },
    },
  },
});

export default GlobalStylingTheme;
